// THE TWO TERMINALS: the box gave the run back (`run_released`), or the work is done and
// the lease is released (`run_finished`).
// Split out of `../task-run-event.ts`, which is now the barrel over this directory.

import { z } from 'zod';

/**
 * The third terminal a run can end on (plan 101 runner lifecycle, slice 01): the BOX
 * gave the claim back. `run_finished` says the work is done; `error` says the attempt
 * failed; `run_released` says neither — the box is going away (a SIGTERM, a
 * self-update that outran the run's ceiling, a lost fleet connection) and the task
 * should be tried again by whoever is up, at the SAME attempt.
 *
 * Its own event rather than an `error` with a special code, because the sink's
 * failure lane (`failOrRetryTask`) spends an attempt on everything it sees, and a
 * restart the task had nothing to do with must not be able to end it: measured on prod
 * over 7 days, 47 runs were aborted by "box is draining" and 8 tasks reached
 * `attempt 3` that way.
 */
export const RUN_RELEASED_EVENT = 'run_released' as const;

/**
 * Why a box gave a run back. Closed: the reason lands in an audit row and a log line.
 *
 *   `box_draining`   — the box is going away (a SIGTERM, a self-update, a lost connection).
 *                      The signal that does NOT name `worker_outdated`: nothing about the
 *                      run's screen is wrong, the box itself is leaving.
 *   `worker_outdated` — the worker cannot decode `screen_items` (H7 #5444, plan 101 PR
 *                      #5446 review). The verb's schema tightened on the worker and this
 *                      box lags the rollout, OR the verb is brand new and the worker
 *                      predates it. Either way, the box cannot screen the run's text and
 *                      gives the claim back without a strike (`apps/runner/src/screen-client.ts`).
 *                      The streak bound on the same `(task, reason)` lives in
 *                      `releaseTaskOnDrain` (#5448): five `task.released` audit rows in an
 *                      hour end the task `box_release_loop` instead.
 *   `plan_area_held` — the run's coordinator planned a file another box-held writer holds
 *                      (`files_plan`, `@developerz/nats`), so the box gave the claim back
 *                      BEFORE any editor wrote. Not the task's fault and not a failure: the
 *                      planned set is now the task's lock, and the claim gate re-admits it
 *                      once that writer is done.
 *   `plan_lock_unavailable` — a claim admitted FOR PLANNING (`assignment.plan_admitted`,
 *                      #5370) holds no area, so its editors may write only after
 *                      `files_plan` answers `locked`; it got no such answer (a fault, a
 *                      refusal, a lane that takes no plan lock), so the box gave the claim
 *                      back rather than edit unlocked.
 *   `paused`         — a person paused the run. It stopped at a step boundary and saved
 *                      what it could. The task waits on this box for Resume
 *                      (pauseTaskOnRelease, which #6748 adds). Until #6748 lands, a
 *                      `run_released{paused}` takes today's drain path (`reconcileRelease`
 *                      re-pends it at the same attempt, counted toward the
 *                      `(task, 'paused')` streak); no runner sends it before R4 (#6750).
 */
export const RUN_RELEASE_REASONS = [
  'box_draining',
  'plan_area_held',
  'plan_lock_unavailable',
  'worker_outdated',
  'paused',
] as const;
export const RunReleaseReasonSchema = z.enum(RUN_RELEASE_REASONS);
export type RunReleaseReason = z.infer<typeof RunReleaseReasonSchema>;

export const RunReleasedPayloadSchema = z.object({
  event: z.literal(RUN_RELEASED_EVENT),
  reason: RunReleaseReasonSchema,
  /**
   * The wall that fired, as the box named it (`box is draining: SIGTERM received`).
   * Scrubbed, then capped, on the box.
   */
  message: z.string().min(1).max(2_000).optional(),
  /**
   * What the executor threw when a drain released the run (#6641), unless it was the drain's
   * own reason or an error whose `cause` is it: a real fault the drain overtook (a provider
   * 500, `gh: HTTP 502`), or a killed child's own exit error after the drain. Absent on a
   * pure drain and an executor's own give-back. Scrubbed, then capped, on the box.
   */
  thrown: z.string().min(1).max(2_000).optional(),
});
export type RunReleasedPayload = z.infer<typeof RunReleasedPayloadSchema>;

/** The happy terminal: the work is done and the box's lease is released. */
export const RUN_FINISHED_EVENT = 'run_finished' as const;

/**
 * What a box reports with its `run_finished` (plan 101 runner lifecycle, slice 07).
 *
 * `run_peak_rss_mb` is the RUNNER PROCESS's peak resident set in whole MiB
 * (`process.resourceUsage().maxRSS`) at the terminal event — a LIFETIME high-water
 * mark of the daemon, so across one box's runs it can only rise; the value on one
 * run is a ceiling on what that run's own heap reached, and the difference between
 * two consecutive runs on the same box is what the later one added. The coding
 * agent's CHILDREN (`bin/check`, `tsc`, `git`) are separate processes and are not in
 * it, which is why a box can report a small number here and still lose a `git push`
 * to exit 137. Absent when the box could not read it; never `0`.
 *
 * `run_workspace_disk_delta_bytes` (#5281) is how much the run GREW the box's workspace
 * filesystem: free bytes there when the run started its work (after the before-pass
 * hygiene) minus free bytes at this terminal. Signed — a run whose after-effects net
 * freed disk reports a negative number, as measured. A NET figure: a file made and
 * deleted inside the run is not in it, so it is a floor under the run's peak. It is the
 * measured half of a repo's per-task disk requirement (`taskDiskRequirement`,
 * `entities/runner/task-disk.ts`). Absent when either reading was unavailable (hygiene
 * off, `statfs` failed); an absent reading is "not measured", never `0`.
 *
 * `run_tree_peak_mb` (2026-10-06) is the run's WHOLE process tree high-water mark in whole
 * MiB, rounded up — the run cgroup's `memory.peak` (`tasks/<runId>`, hierarchical), so the
 * gate's `tsc` and every other child is in it. The input for sizing a box: one slot per
 * box, so a repo's floor is its largest `run_tree_peak_mb` plus the daemon's headroom.
 * Absent on a box without per-run cgroups (an undelegated unit) or a kernel without
 * `memory.peak`; never `0`.
 */
export const RunFinishedPayloadSchema = z.object({
  event: z.literal(RUN_FINISHED_EVENT),
  run_peak_rss_mb: z.number().int().positive().optional(),
  run_tree_peak_mb: z.number().int().positive().optional(),
  run_workspace_disk_delta_bytes: z.number().int().optional(),
});
export type RunFinishedPayload = z.infer<typeof RunFinishedPayloadSchema>;
